import React, { useEffect, useRef, useState } from "react";
import "./Auth.css";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../utils/AuthContext";
import logoPng from "../../assets/LogoDownload.png";
import { account } from "../../appwrite/appwriteConfig";
import { getBaseUrl } from "../../utils/getRedirectUrl";

export default function LogIn() {
    const { user, loginUser, authError, setAuthError, sendRecovery } =
        useAuth(); // Get global error
    const loginForm = useRef(null);
    const recoveryForm = useRef(null);
    const [recovering, setRecovering] = useState(false);
    const [recoveryLoading, setRecoveryLoading] = useState(false);
    const [emailSent, setEmailSent] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        // here check if the user is verified with email and if not navigate hom to /verify
        // console.log("user: ", user);
        if (!user) {
            return;
        }
        if (user?.emailVerification) {
            navigate("/account");
        } else {
            navigate("/verify");
        }
    }, [user, navigate]); // Add dependencies

    const handleSubmit = async (e) => {
        e.preventDefault();
        const email = loginForm.current.email.value;
        const password = loginForm.current.password.value;

        // Basic validation
        if (!email || !password) return;

        await loginUser({ email, password });
    };

    const handleRecovery = () => {
        setRecovering(!recovering);
    };

    const handleSendRecovery = async (e) => {
        e.preventDefault();

        const email = recoveryForm.current.email.value;
        if (!email) return;
        if (emailSent) return;

        setAuthError(null); // Clear previous errors
        setRecoveryLoading(true);

        try {
            await sendRecovery(email);
            setEmailSent(true); // Only show success on successful send
        } catch (error) {
            setAuthError(error?.message || "Failed to send recovery email");
        } finally {
            setRecoveryLoading(false);
        }
    };

    useEffect(() => {
        if (recoveryLoading) {
            console.log("loading...");
        }
    }, [recoveryLoading]);

    return recovering ? (
        <>
            <title>Log In | Oakmate</title>
            <meta
                name="description"
                content="Log in to your Oakmate account to access your game reviews, custom puzzles, and performance statistics."
            />
            <div className="authPage">
                <div className="auth">
                    <div className="auth-component">
                        <img
                            src={logoPng}
                            alt="logoPng"
                            className="logoPng"
                            onClick={() => navigate("/")}
                        />
                        <div className="auth-header-wrapper">
                            <div className="auth-header">Reset Password</div>
                            <div className="auth-subheader">
                                Enter your email address to receive password
                                reset instructions.
                            </div>
                        </div>
                        <form
                            className="auth-form"
                            onSubmit={handleSendRecovery}
                            ref={recoveryForm}
                        >
                            <div className="auth-form-main">
                                <label className="form-field glassy-field">
                                    <input
                                        type="email"
                                        placeholder=" "
                                        name="email"
                                        className="form-input"
                                        autoComplete="email"
                                    />
                                    <span className="form-label">
                                        Your Email
                                    </span>
                                </label>
                            </div>
                            <button type="submit" className="submit-btn">
                                {emailSent ? (
                                    <>
                                        Sent{" "}
                                        <svg
                                            width="10"
                                            height="8"
                                            viewBox="0 0 10 8"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="checkSVG"
                                        >
                                            <path
                                                d="M9.00033 1L3.66699 7L1 4"
                                                stroke="white"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                className="checkSVGPath"
                                            />
                                        </svg>
                                    </>
                                ) : (
                                    "Send"
                                )}
                            </button>
                            {emailSent && (
                                <div className="auth-subheader ok">
                                    The email was succesfully sent to{" "}
                                    {recoveryForm.current.email.value}
                                </div>
                            )}
                            {authError && (
                                <div className="autoFailed">{authError}</div>
                            )}
                            <div
                                className="forgot-password"
                                onClick={handleRecovery}
                            >
                                Back
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    ) : (
        <>
            <title>Log In | Oakmate</title>
            <meta
                name="description"
                content="Log in to your Oakmate account to access your game reviews, custom puzzles, and performance statistics."
            />
            <div className="authPage">
                <div className="auth">
                    <div className="auth-component">
                        <img
                            src={logoPng}
                            alt="logoPng"
                            className="logoPng"
                            onClick={() => navigate("/")}
                        />
                        <div className="auth-header-wrapper">
                            <div className="auth-header">Welcome Back!</div>
                            <div className="auth-subheader">
                                First time here?{" "}
                                <span
                                    className="switchAuthSpan"
                                    onClick={() => {
                                        navigate("/signup");
                                        setAuthError(null);
                                    }}
                                >
                                    Sign up
                                </span>
                            </div>
                        </div>
                        <form
                            className="auth-form"
                            onSubmit={handleSubmit}
                            ref={loginForm}
                        >
                            <div className="auth-form-main">
                                <label className="form-field glassy-field">
                                    <input
                                        type="email"
                                        placeholder=" "
                                        name="email"
                                        className="form-input"
                                        autoComplete="email"
                                    />
                                    <span className="form-label">
                                        Your Email
                                    </span>
                                </label>

                                <label className="form-field glassy-field">
                                    <input
                                        type="password"
                                        placeholder=" "
                                        name="password"
                                        className="form-input"
                                        autoComplete="current-password"
                                    />
                                    <span className="form-label">Password</span>
                                </label>
                            </div>
                            <button type="submit" className="submit-btn">
                                Log In
                            </button>
                            {authError && (
                                <div className="autoFailed">{authError}</div>
                            )}
                            <div
                                className="forgot-password"
                                onClick={handleRecovery}
                            >
                                Forgot Password?
                            </div>
                        </form>
                        <div className="or-wrapper">
                            <div className="auth-separator" />
                            <div className="auth-or-text">OR</div>
                            <div className="auth-separator" />
                        </div>
                        <button
                            className="signInGoogle"
                            onClick={() => {
                                try {
                                    console.log(
                                        "Starting Google OAuth2 session..."
                                    );
                                    const baseUrl = getBaseUrl();
                                    console.log(
                                        "Base URL for OAuth2:",
                                        baseUrl
                                    );
                                    account.createOAuth2Session(
                                        "google",
                                        `${baseUrl}/`,
                                        `${baseUrl}/fail`
                                    );
                                    console.log(
                                        "OAuth2 session creation triggered."
                                    );
                                } catch (error) {
                                    console.error(
                                        "Error during Google OAuth2 session creation:",
                                        error
                                    );
                                }
                            }}
                        >
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="googleIcon"
                            >
                                <path
                                    d="M8 0.380859C9.90889 0.380859 11.5183 1.02914 12.7959 2.10449L11.042 3.85742C10.1829 3.16342 9.14463 2.80469 8 2.80469C5.74333 2.8047 3.83968 4.32568 3.15918 6.35938H3.1582C2.98685 6.87343 2.88477 7.42337 2.88477 8C2.88477 8.57661 2.98685 9.12656 3.1582 9.64062H3.15918C3.83937 11.6734 5.74243 13.2031 8 13.2031C9.1466 13.2031 10.1302 12.8914 10.9102 12.3662C11.8435 11.7436 12.4711 10.8136 12.6797 9.71484L12.7656 9.2627H8.38086V6.92676H15.209C15.2669 7.33165 15.2988 7.74976 15.2988 8.18164C15.2988 10.5247 14.4615 12.4693 13.0361 13.7852C11.7892 14.9362 10.0732 15.6191 8 15.6191C5.02346 15.6191 2.453 13.9028 1.19824 11.4141V11.4131L1.01367 11.0225C0.608752 10.0977 0.380859 9.0781 0.380859 8C0.380862 6.76774 0.678703 5.61214 1.19824 4.58691V4.58594C2.45276 2.09074 5.02296 0.380866 8 0.380859Z"
                                    stroke="white"
                                    strokeWidth="0.761907"
                                />
                            </svg>
                            <div className="signInGoogle-text">
                                Sign in with Google
                            </div>
                        </button>
                        <p className="terms">
                            You acknowledge that you read, and agree, to <br />
                            our{" "}
                            <span className="terms-detail">
                                Terms of Service
                            </span>{" "}
                            and our{" "}
                            <span className="terms-detail">Privacy Policy</span>
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
}
